import { Badge } from "@/components/catalyst/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RelocationFieldsFragment, RelocationStatus } from "@/gql/graphql";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { updateRelocationStatuses } from "@/app/Relocations/GraphQL/relocationMutations";
import { useGetRelocationStatusMap } from "@/app/Dashboard/Utils/relocationStatusMap";
import { relocationStatusColorMap } from "@/app/Relocations/Utils/relocationStatusColorMap";
import { useTranslations } from "use-intl";
import { cn } from "@/lib/utils";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { RelocationEditDialog } from "@/app/Relocations/Components/RelocationEditDialog";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";

export function RelocationStatusBadge({
  relocation,
}: {
  relocation: RelocationFieldsFragment;
}) {
  const { mutateAsync, isPending } = useGqlMutation(updateRelocationStatuses);
  const { open } = useDialog(RelocationEditDialog);
  const relocationStatusMap = useGetRelocationStatusMap();
  const t = useTranslations("relocation");

  const { transitions } = relocationStatusMap[relocation.status];
  const isArchived = relocation.status === RelocationStatus.Archived;

  if (isArchived || transitions.length === 0) {
    return (
      <Badge
        color={relocationStatusColorMap[relocation.status]}
        className="capitalize"
      >
        {t(`status.${relocation.status}`)}
      </Badge>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="focus:outline-none" disabled={isPending}>
          <Badge
            color={relocationStatusColorMap[relocation.status]}
            className="cursor-pointer capitalize"
          >
            {isPending ? <Spinner className="mr-1 h-3 w-3" /> : null}
            {t(`status.${relocation.status}`)}
            <ChevronDownIcon className="ml-1 h-3 w-3" />
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {transitions.map((status) => {
          const { Icon, label, color } = relocationStatusMap[status];
          return (
            <DropdownMenuItem
              key={status}
              onClick={async () => {
                if (
                  status === RelocationStatus.Ready &&
                  [
                    RelocationStatus.SoldOut,
                    RelocationStatus.Expired,
                    RelocationStatus.Paused,
                    RelocationStatus.Draft,
                  ].includes(relocation.status)
                ) {
                  open({
                    id: relocation.id,
                    onSuccess: async () => {
                      await mutateAsync({
                        ids: [relocation.id],
                        status,
                      });
                    },
                  });

                  return;
                }

                await mutateAsync({
                  ids: [relocation.id],
                  status,
                });
              }}
            >
              <div className="flex items-center space-x-2">
                <Icon className={cn("h-4 w-4", color)} />
                <div>{label}</div>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
